/**
 * `<UploadItemRow />` — single row inside the upload tray popover.
 *
 * Subscribes per-item via `useUploadById(id)` so axios progress events on
 * other rows do NOT trigger a re-render here. This is the row half of the
 * ID-array selector pattern: list parents render
 * `<UploadItemRow id={id} />` and only this row reacts when its own item
 * updates. If the item was dismissed during the same tick the parent
 * still renders, we return `null` rather than reading from a stale snapshot.
 *
 * Visual states (driven by `item.status`):
 *   - queued / uploading → progress bar + cancel button
 *   - success            → green check, no actions (auto-dismissed by store)
 *   - failed             → error text + dismiss button
 *   - cancelled          → greyed-out, no actions (auto-dismissed by store)
 *
 * No callbacks plumbed through props — buttons hit
 * `useUploadQueueStore.getState()` directly. This keeps the row decoupled
 * from any wiring concerns.
 */
import { CheckIcon, XIcon } from 'lucide-react';
import { Button, Progress } from '@monksflow/monks-ui';
import { fileIconForMime, formatBytes } from '../../lib/progress';
import { useUploadById } from '../../model/selectors';
import { useUploadQueueStore } from '../../model/upload-queue.store';
import { useTranslation } from 'react-i18next';

interface UploadItemRowProps {
  id: string;
}

export function UploadItemRow({ id }: UploadItemRowProps) {
  const { t } = useTranslation('projects');

  const item = useUploadById(id);
  if (!item) return null;

  const FileIconComponent = fileIconForMime(item.mimeType);
  const isActive = item.status === 'queued' || item.status === 'uploading';
  const isFailed = item.status === 'failed';
  const isSuccess = item.status === 'success';
  const isCancelled = item.status === 'cancelled';

  return (
    <div
      className={`flex items-start gap-3 px-3 py-2 ${isCancelled ? 'opacity-60' : ''}`}
      data-testid="upload-row"
      data-status={item.status}
    >
      <FileIconComponent
        className="mt-0.5 size-4 shrink-0 text-muted-foreground"
        data-testid="upload-row-file-icon"
        data-mime={item.mimeType}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-medium">{item.fileName}</span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {formatBytes(item.fileSize)}
          </span>
        </div>

        {isActive && (
          <div className="mt-1.5">
            <Progress
              value={item.progress}
              aria-label={t('uploadQueue.uploadItemRow.uploadProgress', {
                progress: item.progress,
              })}
            />
          </div>
        )}

        {isFailed && item.error && (
          <p className="mt-1 text-xs text-destructive" role="alert">
            {item.error}
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {isActive && (
          <Button
            variant="ghost"
            size="mini"
            aria-label={t('uploadQueue.uploadItemRow.cancelUpload')}
            onClick={() => useUploadQueueStore.getState().cancel(item.id)}
          >
            <XIcon className="size-3.5" />
          </Button>
        )}

        {isFailed && (
          <Button
            variant="ghost"
            size="mini"
            aria-label={t('uploadQueue.uploadItemRow.dismissUpload')}
            onClick={() => useUploadQueueStore.getState().dismiss(item.id)}
          >
            <XIcon className="size-3.5" />
          </Button>
        )}

        {isSuccess && (
          <span
            data-testid="upload-row-success-check"
            aria-label={t('uploadQueue.uploadItemRow.uploadComplete')}
          >
            <CheckIcon className="size-4 text-green-600" />
          </span>
        )}
      </div>
    </div>
  );
}
